import React, { useRef, useMemo, memo, useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  addMonths, 
  isSameMonth, 
  startOfWeek, 
  endOfWeek,
  isToday,
  startOfDay,
  isValid,
  isSameDay,
  addDays,
  differenceInDays
} from 'date-fns';

const TOTAL_MONTHS = 2400; 
const START_INDEX = 1200; 

const MonthGrid = memo(({ monthDate, events, onEventClick, onDateClick }) => {
    if (!monthDate || !isValid(monthDate)) return null;

    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    
    const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
        weeks.push(calendarDays.slice(i, i + 7));
    }

    const monthEvents = (events || []).filter(e => {
        const eStart = new Date(e.start);
        if (!isValid(eStart)) return false;
        const eEnd = e.end ? new Date(e.end) : eStart;
        return (eStart <= endDate && eEnd >= startDate);
    });

    return (
        <div className="scroll-month-section" style={{ background: 'white', paddingBottom: '20px' }}>
            {/* Sticky Month Title */}
            <h2 className="scroll-month-title" style={{ 
                padding: '15px 20px', 
                margin: 0, 
                background: 'rgba(255, 255, 255, 0.95)', 
                backdropFilter: 'blur(10px)',
                fontSize: '1.5rem', 
                fontWeight: '700',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                borderBottom: '0.5px solid #efeff4'
            }}>
                {format(monthDate, 'MMMM yyyy')}
            </h2>
            <div className="scroll-month-grid" style={{ width: '100%' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', width: '100%', borderBottom: '0.5px solid #efeff4', background: '#f8f8f8' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                        <div key={i} className="scroll-day-header" style={{ textAlign: 'center', padding: '8px 0', fontSize: '0.7rem', fontWeight: '600', color: '#8e8e93', textTransform: 'uppercase' }}>{d}</div>
                    ))}
                </div>
                {weeks.map((week, idx) => (
                    <WeekRow 
                        key={`${format(monthDate, 'yyyy-MM')}-w-${idx}`} 
                        week={week} 
                        monthDate={monthDate} 
                        events={monthEvents} 
                        onEventClick={onEventClick}
                        onDateClick={onDateClick}
                    />
                ))}
            </div>
        </div>
    );
});

const WeekRow = ({ week, monthDate, events, onEventClick, onDateClick }) => {
    const weekStart = week[0];
    const weekEnd = week[6];

    const segments = useMemo(() => {
        const rowSegments = [];
        const tracks = [];

        const weekEvents = events.filter(e => {
            const eStart = startOfDay(new Date(e.start));
            const eEnd = e.end ? startOfDay(new Date(e.end)) : eStart;
            return eStart <= weekEnd && eEnd >= weekStart;
        }).sort((a, b) => {
            const durA = differenceInDays(new Date(a.end || a.start), new Date(a.start));
            const durB = differenceInDays(new Date(b.end || b.start), new Date(b.start));
            if (durA !== durB) return durB - durA;
            return new Date(a.start) - new Date(b.start);
        });

        weekEvents.forEach(event => {
            const eStart = startOfDay(new Date(event.start));
            const eEnd = event.end ? startOfDay(new Date(event.end)) : eStart;
            
            const startCol = Math.max(0, differenceInDays(eStart, weekStart));
            const endCol = Math.min(6, differenceInDays(eEnd, weekStart));
            
            let trackIdx = 0;
            while (true) {
                if (!tracks[trackIdx]) tracks[trackIdx] = new Array(7).fill(false);
                let available = true;
                for (let i = startCol; i <= endCol; i++) {
                    if (tracks[trackIdx][i]) {
                        available = false;
                        break;
                    }
                }
                if (available) {
                    for (let i = startCol; i <= endCol; i++) tracks[trackIdx][i] = true;
                    break;
                }
                trackIdx++;
            }

            rowSegments.push({
                event,
                startCol,
                endCol,
                trackIdx,
                isStart: isSameDay(eStart, addDays(weekStart, startCol)),
                isEnd: isSameDay(eEnd, addDays(weekStart, endCol))
            });
        });

        return rowSegments;
    }, [events, weekStart, weekEnd]);

    return (
        <div style={{ position: 'relative', minHeight: '120px', borderBottom: '0.5px solid #efeff4' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: '100%', position: 'absolute', top: 0, left: 0, right: 0 }}>
                {week.map((day, i) => {
                    const isCurrentMonth = isSameMonth(day, monthDate);
                    return (
                        <div 
                            key={i} 
                            onClick={() => onDateClick && onDateClick({ date: day })}
                            style={{ 
                                borderRight: i < 6 ? '0.5px solid #efeff4' : 'none',
                                background: isCurrentMonth ? 'white' : '#fcfcfc',
                                cursor: 'pointer'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '8px' }}>
                                <span style={{ 
                                    fontSize: '0.9rem',
                                    fontWeight: isToday(day) ? '700' : '400',
                                    color: isToday(day) ? 'white' : (isCurrentMonth ? 'black' : '#c7c7cc'),
                                    backgroundColor: isToday(day) ? '#ff3b30' : 'transparent',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {format(day, 'd')}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)', 
                gridAutoRows: '24px', 
                gap: '2px', 
                paddingTop: '36px',
                paddingBottom: '4px',
                pointerEvents: 'none'
            }}>
                {segments.map((seg, idx) => (
                    <div 
                        key={`${seg.event.id}-seg-${idx}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEventClick({ event: seg.event });
                        }}
                        style={{ 
                            gridColumn: `${seg.startCol + 1} / ${seg.endCol + 2}`,
                            gridRow: seg.trackIdx + 1,
                            backgroundColor: seg.event.color || '#0071e3',
                            color: 'white',
                            fontSize: '0.7rem',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginLeft: seg.isStart ? '4px' : '0',
                            marginRight: seg.isEnd ? '4px' : '0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            fontWeight: '500',
                            zIndex: 2,
                            pointerEvents: 'auto',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                    >
                        {seg.isStart ? seg.event.title : '\u00A0'}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function InfiniteScrollCalendar({ events = [], onEventClick, onDateClick, initialDate, scrollToTodayTrigger, onTitleChange }) {
    const virtuosoRef = useRef(null);
    const baseDate = useMemo(() => {
        const d = initialDate ? new Date(initialDate) : new Date();
        return isValid(d) ? d : new Date();
    }, [initialDate]);
    
    const getMonthDate = (index) => {
        return addMonths(baseDate, index - START_INDEX);
    };

    const handleRangeChanged = (range) => {
        if (onTitleChange) {
            const firstMonth = getMonthDate(range.startIndex);
            onTitleChange(format(firstMonth, 'MMMM yyyy'));
        }
    };

    useEffect(() => {
        if (scrollToTodayTrigger && virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({
                index: START_INDEX,
                align: 'start',
                behavior: 'smooth'
            });
        }
    }, [scrollToTodayTrigger]);

    return (
        <div className="scroll-calendar-container" style={{ height: '80vh', width: '100%', background: 'white', overflow: 'hidden', border: '1px solid #ddd', borderRadius: '12px' }}>
            <Virtuoso
                ref={virtuosoRef}
                style={{ height: '100%', width: '100%' }}
                totalCount={TOTAL_MONTHS}
                initialTopMostItemIndex={START_INDEX}
                overscan={1200}
                rangeChanged={handleRangeChanged}
                itemContent={(index) => (
                    <MonthGrid 
                        monthDate={getMonthDate(index)} 
                        events={events} 
                        onEventClick={onEventClick}
                        onDateClick={onDateClick}
                    />
                )}
            />
        </div>
    );
}
